<template>
  <div class="container pb-20 pt-60 md:pt-80">
    <h1 class="text-header1 md:text-desktop-header1">
      {{ page?.data.title }}
    </h1>

    <!-- Filters -->
    <div class="my-10 flex flex-wrap items-start gap-4">
      <!-- Categories -->
      <div
        v-for="category in categories"
        :key="category.value"
        class=""
      >
        <input
          :id="category.label"
          v-model="checkedCategories"
          type="radio"
          :name="category.label"
          :value="category.value"
          class="peer hidden"
        />
        <label
          :for="category.label"
          class="cursor-pointer select-none border-b-2 border-charcoal-100 p-1 font-bold text-charcoal-100 opacity-30 transition-opacity duration-200 ease-in-out peer-checked:opacity-100 md:px-4 md:py-2"
        >{{ category.label }}</label>
      </div>
    </div>

    <!-- Results -->
    <div
      v-if="events?.length"
    >
      <transition-group name="list">
        <div
          v-for="event in events"
          :key="event.uid"
        >
          <EventsCard :event="event" />
        </div>
      </transition-group>
    </div>

    <!-- No Results -->
    <div
      v-else
      class="text-center"
    >
      <h2 class="text-header2 md:text-desktop-header2">
        There are no results.
      </h2>
      <p>Please change your search criteria.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import dayjs from "dayjs";

const prismic = usePrismic();
const route = useRoute();
const config = useRuntimeConfig();

const checkedCategories = ref("*");

const categories = [
  { label: "All", value: "*" },
  { label: "Exhibitions", value: "Exhibition" },
  { label: "Workshops", value: "Workshop" },
  { label: "Talks", value: "Talk" },
  { label: "Tours", value: "Tour" },
];

const { data: page } = useAsyncData("whats-on", () =>
  prismic.client.getByUID("pages", "events"),
);

const { data: events } = await useAsyncData(
  "events",
  async () => {
    const events = await prismic.client.getByType("events", {
      pageSize: 50,
      orderings: [
        {
          field: "my.events.start_date",
          direction: "asc",
        },
      ],
      filters: [
        prismic.filter.dateAfter("my.events.end_date", dayjs().format()),
        (checkedCategories.value != "*") ? prismic.filter.at("my.events.type", checkedCategories.value) : "",
      ],
    });

    if (events.results.length) {
      return events.results;
    } else {
      return [];
    }
  }, {
    watch: [checkedCategories],
  });

useSeoMeta({
  description: () => page.value?.data.meta_description?.length ? page.value.data.meta_description : prismic.asText(page.value?.data.description),
  ogTitle: () => page.value?.data.meta_title?.length ? page.value.data.meta_title : page.value?.data.title,
  ogUrl: () => `${config.public.BASE_URL}${route.fullPath}`,
  ogDescription: () => page.value?.data.meta_description?.length ? page.value.data.meta_description : prismic.asText(page.value?.data.description),
  ogImage: () => page.value?.data.meta_image.url?.length ? page.value.data.meta_image.url : `${config.public.BASE_URL}/lightbox_gallery.jpg`,
  ogImageType: () => "image/jpeg",
  ogImageWidth: () => "1200",
  ogImageHeight: () => "630",
  ogType: () => "website",
  twitterTitle: () => page.value?.data.meta_title?.length ? page.value.data.meta_title : page.value?.data.title,
  twitterImage: () => page.value?.data.meta_image.url?.length ? page.value.data.meta_image.url : `${config.public.BASE_URL}/lightbox_gallery.jpg`,
  twitterDescription: () => page.value?.data.meta_description?.length ? page.value.data.meta_description : prismic.asText(page.value?.data.description),
});
</script>
